import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Wifi, Laptop, Plug, Sun, Clock, TrainFront, MapPin } from "lucide-react";
import { AMENITIES, BUSINESS } from "../data/content";
import { LOCATIONS } from "../data/locations";
import { SectionLabel } from "./Section";
import { fadeUp, stagger, viewport } from "../lib/motion";

const ICONS = { Wifi, Laptop, Plug, Sun, Clock, TrainFront };

const WorkFriendly = () => (
  <section id="work" className="relative py-16 sm:py-32 bg-surface/40" data-testid="work-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Work-friendly amenities */}
      <div className="max-w-2xl mb-14">
        <SectionLabel>Work · Meet · Linger</SectionLabel>
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]"
        >
          Built for the way <span className="text-highlight">Arlington works.</span>
        </motion.h2>
        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="mt-5 text-foreground/70 text-base sm:text-lg leading-relaxed"
        >
          Morning meetings, focused work sessions, or an unhurried afternoon on the patio — Detour is the neighborhood's favorite place to plug in and stay awhile.
        </motion.p>
      </div>

      <motion.div
        variants={stagger}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
      >
        {AMENITIES.map((a, i) => {
          const Icon = ICONS[a.icon];
          return (
            <motion.div
              key={a.title}
              variants={fadeUp}
              whileHover={{ y: -5 }}
              transition={{ type: "spring", stiffness: 300, damping: 22 }}
              className="rounded-3xl border border-surface bg-background p-7 shadow-warm"
              data-testid={`amenity-card-${i}`}
            >
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-surface text-primary">
                {Icon && <Icon size={22} strokeWidth={1.6} />}
              </div>
              <h3 className="mt-6 text-xl">{a.title}</h3>
              <p className="mt-2 text-sm text-foreground/65 leading-relaxed">{a.copy}</p>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Areas We Serve — local SEO */}
      <motion.div
        id="areas"
        variants={fadeUp}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
        className="mt-8 scroll-mt-28 rounded-3xl bg-primary text-background p-8 sm:p-12"
        data-testid="areas-served"
      >
        <div className="flex items-center gap-3 mb-4">
          <MapPin size={18} className="text-highlight" strokeWidth={1.8} />
          <span className="text-xs uppercase tracking-[0.24em] font-semibold text-highlight">Areas We Serve</span>
        </div>
        <h3 className="text-2xl sm:text-3xl lg:text-4xl leading-tight max-w-3xl">
          Proudly serving Arlington & <span className="text-highlight">Northern Virginia.</span>
        </h3>
        <p className="mt-4 max-w-2xl text-background/75 leading-relaxed">
          Just off Wilson Boulevard at {BUSINESS.address}, Detour Coffee Co. is a short walk or drive for coffee lovers, remote workers and neighbors across Arlington — including these nearby communities:
        </p>
        <div className="mt-7 flex flex-wrap gap-2.5">
          {LOCATIONS.map((loc) => (
            <Link
              key={loc.slug}
              to={`/coffee-shop/${loc.slug}`}
              data-testid={`area-${loc.slug}`}
              className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-sm text-background/90 transition-colors hover:bg-highlight hover:text-foreground hover:border-highlight"
            >
              {loc.name}
            </Link>
          ))}
        </div>
      </motion.div>
    </div>
  </section>
);

export default WorkFriendly;
